import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import Treemap, {
  TreemapLegend,
  type TreemapTile,
  type TreemapTooltip,
} from '@/components/charts/Treemap'
import { formatChange, formatShare, roundChange } from '@/components/charts/treemap-bands'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import RefreshRegion from '@/components/ui/RefreshRegion'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import type { KeyValueRow } from '@/components/ui/KeyValueList'
import { useHeatmap, useHeatmapPrefetch } from '@/features/heatmap/api/queries'
import HeatmapControls from '@/features/heatmap/components/HeatmapControls'
import { formatQuantity } from '@/lib/format'
import { sentenceCase } from '@/lib/text'
import { cn } from '@/lib/utils'
import { heatmapSearchSchema, loadHeatmap } from '@/screens/market/HeatmapScreen.loader'
import type { HeatmapQuery, HeatSizing, HeatTile } from '@/schemas/heatmap'

const HEADER = <TitleRow title="Market Heatmap" />

/**
 * The controls row, the map card, and the legend and source line that caption it, one block 16px
 * apart throughout: the pages revamp's one gap from a toolbar to its first block, which is also
 * the frame's spacing under the card, snapped to the 4-grid — close enough to read as the card's
 * key rather than as two more sections of the page.
 */
const MAP_BLOCK = 'flex flex-col gap-3'

/** The map's card: the mock pads it 24 on every side, where `SECTION_CARD` is 20 across and 16 down. */
const MAP_CARD = cn(
  SECTION_CARD,
  '[--section-card-pad-x:var(--space-6)] [--section-card-pad-y:var(--space-6)]',
)

/**
 * The card's height replicates `Treemap`'s own `heightFor` (drawn 620,
 * portrait 1.35× the box width below a 1024px box — see that function's own
 * comment): `@5xl` is this app's container scale for 64rem/1024px, the same
 * threshold, off the SAME unnamed `@container` the card already sizes its own
 * padding against (`SECTION_CARD`'s `@max-sm` step). Measured against the rendered page at 1440
 * (622) and at 390 (450) to confirm the ratio, not just the formula.
 *
 * Not the loaded page's own invisible-ghost-`Treemap` trick (`MapFailed`,
 * `MapEmpty`): that imports the chart component, and a route's
 * `pendingComponent` is not code-split — see docs/route-loading.md, "Pitfalls".
 */
function HeatmapCardSkeleton() {
  return (
    <div className={MAP_CARD}>
      <Skeleton
        className={cn(
          'aspect-[1/1.35] max-h-155 min-h-90 w-full rounded-md',
          '@5xl:aspect-auto @5xl:h-155 @5xl:max-h-none @5xl:min-h-0',
        )}
      />
    </div>
  )
}

/**
 * The controls row (the crumb and the sizing control, 36), the card, the legend line and the
 * source line, in the same `MAP_BLOCK` stack the loaded page uses.
 */
function HeatmapPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the heatmap…"
      header={HEADER}
      pageTitle="Market Heatmap"
    >
      <div className={MAP_BLOCK}>
        <div className="flex min-h-9 items-center justify-between gap-3">
          <Skeleton className="h-4 w-56 max-w-full" />
          <Skeleton className="h-9 w-60 max-w-full" />
        </div>
        <HeatmapCardSkeleton />
        <div className="flex h-4 items-center gap-3">
          <Skeleton className="h-4 w-72 max-w-full rounded-xs" />
        </div>
        <div className="flex h-4 items-center">
          <Skeleton className="h-3 w-96 max-w-full rounded-xs" />
        </div>
      </div>
    </ScreenLayout>
  )
}

function HeatmapError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load the heatmap"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/heatmap')({
  validateSearch: heatmapSearchSchema,
  loader: loadHeatmap,
  pendingComponent: HeatmapPending,
  errorComponent: HeatmapError,
  component: HeatmapScreen,
})

/**
 * What tile area stands for, in the legend's own words.
 *
 * Written here rather than hard-coded as "market cap" because the chip
 * changes it: a legend that says market cap under the Turnover chip
 * contradicts the source line printed directly beneath it.
 */
const SIZING_WORDS: Record<HeatSizing, string> = {
  cap: 'market cap',
  turnover: 'turnover',
  volume: 'volume',
}

/** The URL's depth and sizing, in the shape the map is cached under. */
const queryFor = (sector: string | undefined, sizing: HeatSizing): HeatmapQuery => ({
  sizing,
  ...(sector === undefined ? {} : { sector }),
})

/** Which way the map just moved: into a sector, back out of one, or neither. */
type Entrance = 'fade' | 'zoom-in' | 'zoom-out'

/**
 * Opacity and scale only, 200ms out of the way, and none at all under
 * reduced motion. Zooming in, the names grow into place from 97%; zooming
 * out, the sectors settle from 103% — the direction the member just moved.
 * A sizing change or a first paint only fades.
 */
const ENTRANCE: Record<Entrance, string> = {
  fade: 'starting:opacity-0',
  'zoom-in': 'starting:scale-[0.97] starting:opacity-0',
  'zoom-out': 'starting:scale-[1.03] starting:opacity-0',
}

interface Shown {
  /** Depth and sizing — a new key is a new map, and a new entrance. */
  key: string
  sector: string | undefined
  entrance: Entrance
  /** The sector the member just came back out of, to hand focus back to. */
  cameFrom: string | undefined
}

/**
 * Remembers the last map drawn so the next one knows which way it came.
 * Set during render — React's "adjusting state when a prop changes" — so the
 * entrance is decided in the same render that draws the new map, not one
 * frame after it.
 */
function useEntrance(query: HeatmapQuery | undefined): Shown | null {
  const [shown, setShown] = useState<Shown | null>(null)
  if (query === undefined) return shown

  const key = `${query.sector ?? ''}|${query.sizing}`
  if (shown?.key === key) return shown

  const depthChanged =
    shown !== null && (shown.sector === undefined) !== (query.sector === undefined)
  const next: Shown = {
    key,
    sector: query.sector,
    entrance: !depthChanged ? 'fade' : query.sector === undefined ? 'zoom-out' : 'zoom-in',
    cameFrom: depthChanged ? shown.sector : undefined,
  }
  setShown(next)
  return next
}

/**
 * A tile as the map draws it. A sector's names print as symbols — what the
 * frame draws, and what fits — with the company behind each kept for its
 * accessible name and panel.
 *
 * Neither missing figure is drawn as a zero. A tile with no size has no area
 * of its own, and `layoutFlushRows` still gives it the smallest tile a pointer
 * can land on; one with no move takes the neutral tone rather than the Flat
 * band, which would claim it did not move.
 */
const toTreemapTile = (tile: HeatTile, isSector: boolean): TreemapTile => ({
  id: tile.id,
  ...(isSector || tile.name === tile.id
    ? { label: sentenceCase(tile.name) }
    : { label: tile.id, fullName: tile.name }),
  weight: tile.size ?? 0,
  ...(tile.percentChange === null ? { tone: 'neutral' } : { percentChange: tile.percentChange }),
})

/** What a tile's popover adds to its name and move, beyond the face. */
interface TileFacts {
  /** Of the whole market's cap for a sector, of its sector's total for a name; 0 to 1. */
  share: number | undefined
  names: number | undefined
}

/**
 * The popover on every tile, the reference's (`mkc-hm-tip`): the name, today's
 * move, its share, a sector's count of names, and on a sector what pressing
 * it does. A line whose figure mm-be has no record of is left out rather than
 * printed as a blank or a zero (owner decision, 2026-10-05).
 */
const popoverFor = (
  tile: TreemapTile,
  facts: TileFacts | undefined,
  shareLabel: string,
  isSector: boolean,
): TreemapTooltip => {
  const change = tile.percentChange === undefined ? undefined : roundChange(tile.percentChange)
  const rows: KeyValueRow[] = []
  if (change !== undefined) {
    rows.push({
      label: 'Today',
      value: formatChange(change),
      tone: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
    })
  }
  if (facts?.share !== undefined) rows.push({ label: shareLabel, value: formatShare(facts.share) })
  if (facts?.names !== undefined) rows.push({ label: 'Names', value: formatQuantity(facts.names) })
  return {
    title: tile.fullName === undefined ? tile.label : `${tile.label} · ${tile.fullName}`,
    rows,
    ...(isSector ? { hint: 'Click to open its names' } : {}),
  }
}

/**
 * Each tile's share and count, off the map on screen. A sector's share is
 * mm-be's own `market_cap_share`, whatever the chip — the figure its drill-in
 * caption prints, so the two agree. A name's is its size over the sector's
 * total for the chip on screen, which is what its tile's area already shows;
 * a name with no size on record has none.
 */
const factsFor = (tiles: readonly HeatTile[], isSectors: boolean): Map<string, TileFacts> => {
  const total = tiles.reduce((sum, tile) => sum + (tile.size ?? 0), 0)
  return new Map(
    tiles.map((tile) => [
      tile.id,
      {
        share: isSectors
          ? tile.marketCapShare === undefined
            ? undefined
            : tile.marketCapShare / 100
          : tile.size === null || total <= 0
            ? undefined
            : tile.size / total,
        names: tile.nameCount,
      },
    ]),
  )
}

/** The share row's label: the market's cap at the top, the sector's own sizing inside one. */
const shareLabelFor = (isSectors: boolean, sizing: HeatSizing): string =>
  isSectors ? 'Share of market cap' : `Share of sector ${SIZING_WORDS[sizing]}`

/**
 * The card body when the first map never arrived. The ghost map underneath is
 * invisible and there only to hold the card at the height the map will have,
 * so the legend under it does not move on the way in or on retry.
 */
function MapFailed({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  return (
    <div className="relative">
      <div aria-hidden="true" className="invisible [&_*]:animate-none">
        <Treemap tiles={[]} ariaLabel="" state="loading" variant="heatmap" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <EmptyState
          kind="failed"
          title="Couldn't load the heatmap"
          description="Check your connection and try again."
          onRetry={onRetry}
          retrying={retrying}
        />
      </div>
    </div>
  )
}

/**
 * The card body when a map comes back with no tiles, held at the map's height
 * the way `MapFailed` is.
 *
 * Inside a drill-in that is a sector key mm-be does not know — it answers
 * those with a 200 and no rows, so it is a wrong or stale link, not a failure,
 * and the way out is the top level. A real sector always has names.
 */
function MapEmpty({ sector, sizing }: { sector: string | undefined; sizing: HeatSizing }) {
  return (
    <div className="relative">
      <div aria-hidden="true" className="invisible [&_*]:animate-none">
        <Treemap tiles={[]} ariaLabel="" state="loading" variant="heatmap" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        {sector === undefined ? (
          <EmptyState
            title="No sectors to map yet"
            description="The map fills in once prices are in."
          />
        ) : (
          <EmptyState
            kind="not-found"
            title="No names in this sector"
            description="The link may be wrong, or the sector was removed."
            action={
              <Link
                to="/market/heatmap"
                search={{ sizing }}
                className={stateActionClass('section')}
              >
                All sectors
              </Link>
            }
          />
        )}
      </div>
    </div>
  )
}

/**
 * Market heatmap, against its V2 frames: all sectors (`4243:106729`) and one
 * sector opened (`4243:118584`). Header, controls, card, legend and source
 * line hold one layout at both depths and in every state — loading, failed,
 * a drill-in on its way — so pressing a tile changes what is in the map and
 * nothing around it.
 */
function HeatmapScreen() {
  const { sector, sizing } = Route.useSearch()
  const navigate = Route.useNavigate()

  const heatmap = useHeatmap(queryFor(sector, sizing))
  const prefetch = useHeatmapPrefetch()
  const view = heatmap.data
  // `keepPreviousData` is holding the last map while this one loads.
  const pending = heatmap.isPlaceholderData
  const shown = useEntrance(view?.query)
  const mapRef = useRef<HTMLDivElement>(null)

  // The header and crumb follow the URL at once; the words that describe a
  // map wait for that map. A view of the same depth (a sizing change still
  // loading) describes it well enough — the caption only differs in its
  // weighted move. A view of the other depth does not, so the caption and
  // source hold their line empty rather than print the last depth's words.
  const describesUrl = view !== undefined && view.query.sector === sector
  const rawSectorName = describesUrl
    ? view.map.sectorName
    : view?.map.tiles.find((tile) => tile.id === sector)?.name
  // The API writes sectors in Title Case; the mock, like the rest of the app, in sentence case.
  const sectorName = rawSectorName === undefined ? undefined : sentenceCase(rawSectorName)

  const viewIsSectors = view?.query.sector === undefined
  const tiles = useMemo(
    () =>
      (view?.map.tiles ?? []).map((tile) => toTreemapTile(tile, view?.query.sector === undefined)),
    [view],
  )
  // Off the map on screen, which during a chip change is the last one until
  // the next lands.
  const facts = useMemo(
    () => factsFor(view?.map.tiles ?? [], view?.query.sector === undefined),
    [view],
  )
  const shareLabel = shareLabelFor(viewIsSectors, view?.query.sizing ?? sizing)

  // Pressing a tile replaces it, so a keyboard member's focus would fall to
  // the page and their next Tab would start from the top. Only when focus was
  // actually lost: the new map takes it, or on the way back out, the sector
  // tile they went in through.
  useEffect(() => {
    if (shown === null || shown.entrance === 'fade') return
    const active = document.activeElement
    if (active !== null && active !== document.body) return
    const group = mapRef.current
    const cameFrom = [...(group?.querySelectorAll<HTMLElement>('[data-tile-id]') ?? [])].find(
      (tile) => tile.dataset['tileId'] === shown.cameFrom,
    )
    ;(cameFrom ?? group)?.focus({ preventScroll: true })
  }, [shown])

  return (
    <ScreenLayout header={HEADER} pageTitle="Market Heatmap">
      <div className={MAP_BLOCK}>
        <HeatmapControls
          {...(sector === undefined ? {} : { sector })}
          {...(sectorName === undefined ? {} : { sectorName })}
          {...(describesUrl ? { caption: view.map.caption } : {})}
          sizing={sizing}
          onSizingChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, sizing: next }) })
          }}
        />

        <div aria-busy={pending || undefined} className={MAP_CARD}>
          <p role="status" className="sr-only">
            {pending ? 'Loading the map…' : ''}
          </p>
          {view === undefined ? (
            heatmap.isError ? (
              <MapFailed
                onRetry={() => {
                  void heatmap.refetch()
                }}
                retrying={heatmap.isFetching}
              />
            ) : (
              <Treemap tiles={[]} ariaLabel="Market heatmap" state="loading" variant="heatmap" />
            )
          ) : view.map.tiles.length === 0 ? (
            // Leaving an empty map there is nothing to hold up while the next
            // one loads, so it gets the ghost a first load gets.
            pending ? (
              <Treemap tiles={[]} ariaLabel="Market heatmap" state="loading" variant="heatmap" />
            ) : (
              <MapEmpty sector={view.query.sector} sizing={sizing} />
            )
          ) : (
            // The last map stays up while the next loads, as it was and still
            // live: pressing another tile takes the member there instead, the
            // newest press winning. A wait past 300ms draws the refresh line. A
            // new depth is a new map and enters; a new sizing is the same map,
            // so it stays mounted and its tiles morph to their new sizes — the
            // map's own transitions, so no crossfade over them.
            <RefreshRegion updating={pending} crossfade={false}>
              <div
                key={shown?.sector ?? ''}
                className={cn(
                  'transition-[opacity,scale] duration-med ease-out motion-reduce:transition-none',
                  ENTRANCE[shown?.entrance ?? 'fade'],
                )}
              >
                {/* Every tile is a real link: a sector opens its names, a name
                  opens its stock page, and middle-click / "open in new tab"
                  work on both. What a tile links to is decided by the map it
                  belongs to, never by the URL — while a drill-in loads, the
                  tiles on screen are still sectors. Sizing rides along on the
                  sector link because `search` spreads `prev`. */}
                <Treemap
                  ref={mapRef}
                  variant="heatmap"
                  tiles={tiles}
                  ariaLabel={
                    viewIsSectors ? 'Sectors by size and today’s move' : 'Names in this sector'
                  }
                  tileLinkOptions={(tile) =>
                    viewIsSectors
                      ? { to: '/market/heatmap', search: (prev) => ({ ...prev, sector: tile.id }) }
                      : { to: '/stocks/$symbol', params: { symbol: tile.id } }
                  }
                  tileTooltip={(tile) =>
                    popoverFor(tile, facts.get(tile.id), shareLabel, viewIsSectors)
                  }
                  {...(viewIsSectors
                    ? {
                        onTileIntent: (tile: TreemapTile) => {
                          prefetch(queryFor(tile.id, sizing))
                        },
                      }
                    : {})}
                />
              </div>
            </RefreshRegion>
          )}
        </div>

        <TreemapLegend
          variant="heatmap"
          caption={`Tile size is ${SIZING_WORDS[sizing]} · colour is today’s move`}
        />
        {describesUrl ? (
          <p className="text-body-xs text-muted-foreground-subtle">{view.map.source}</p>
        ) : (
          // The source line's own 16px, so nothing under it moves when it lands.
          <div className="flex h-4 items-center">
            <Skeleton className="h-3 w-80 max-w-full" />
          </div>
        )}
      </div>
    </ScreenLayout>
  )
}
